import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('phone_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim()) {
      setFormStatus('success');
      form.reset();
      return;
    }

    const email = formData.get('email');
    const name = formData.get('name');
    const message = formData.get('message');

    if (!email || !name || !message) {
      setFormStatus('error');
      setFormError('请填写所有必填项');
      return;
    }

    // Simulate form submission
    setFormStatus('success');
    form.reset();
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黑料吃瓜网',
    url: 'http://9288c.com/',
    logo: 'http://9288c.com/logo.png',
    description: '全网热门黑料事件汇总与最新吃瓜爆料资讯一站式浏览平台',
    email: 'hello@9288c.com',
    sameAs: ['http://9288c.com/'],
  };

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 黑料吃瓜网',
    description: '了解黑料吃瓜网的定位、价值观和联系方式',
    url: 'http://9288c.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '黑料吃瓜网',
      url: 'http://9288c.com/',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://9288c.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
        item: 'http://9288c.com/about',
      },
    ],
  };

  const contactSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: '联系我们 - 黑料吃瓜网',
    description: '联系黑料吃瓜网，提供爆料、建议或投诉',
    url: 'http://9288c.com/about#contact',
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([orgSchema, aboutPageSchema, breadcrumbSchema, contactSchema]),
        }}
      />

      <Navbar />

      <main className="pt-20 md:pt-24" role="main">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Page Header */}
          <div className="mt-6 md:mt-8 text-center mb-10 md:mb-14">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-fire-line text-background-50 text-3xl md:text-4xl" />
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
              关于黑料吃瓜网
            </h1>
            <p className="text-foreground-500 max-w-lg mx-auto text-sm md:text-base">
              全网热门黑料事件汇总与最新吃瓜爆料资讯一站式浏览平台
            </p>
          </div>

          {/* Brand Story */}
          <section className="mb-12 md:mb-16" aria-labelledby="brand-heading">
            <h2
              id="brand-heading"
              className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4 flex items-center gap-2"
            >
              <i className="ri-information-line text-primary-500" />
              品牌介绍
            </h2>
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6">
              <p className="text-foreground-700 leading-relaxed mb-4 text-sm md:text-base">
                黑料吃瓜网成立于2024年，是国内领先的娱乐八卦资讯聚合平台。我们致力于为用户提供全网最新、最热、最全的吃瓜资讯，涵盖明星塌房、网红反差、不雅内幕、劲爆聊天记录等各大热门板块。
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4 text-sm md:text-base">
                凭借专业的编辑团队和广泛的信息渠道，黑料吃瓜网能够在热点事件发生的第一时间进行跟进报道，为用户提供最真实、最全面的吃瓜内容。我们的口号是"精彩不容错过，速来围观"，力求让每一位用户都能在这里找到自己感兴趣的吃瓜内容。
              </p>
              <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                目前，黑料吃瓜网已积累超过50万注册用户，日均访问量突破百万，是国内最具影响力的吃瓜资讯平台之一。我们的内容不仅覆盖PC端，还同步更新至各大社交媒体平台，确保用户无论身处何地都能第一时间获取最新吃瓜资讯。
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="mb-12 md:mb-16" aria-labelledby="stats-heading">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {[
                { icon: 'ri-user-line', number: '50万+', label: '注册用户' },
                { icon: 'ri-eye-line', number: '100万+', label: '日均访问' },
                { icon: 'ri-article-line', number: '3000+', label: '原创文章' },
                { icon: 'ri-fire-line', number: '99%', label: '用户满意度' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-50 rounded-xl border border-background-200/70 p-4 md:p-5 text-center"
                >
                  <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <i className={`${stat.icon} text-primary-500`} />
                  </div>
                  <div className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                    {stat.number}
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Categories Overview */}
          <section className="mb-12 md:mb-16" aria-labelledby="categories-heading">
            <h2
              id="categories-heading"
              className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4 flex items-center gap-2"
            >
              <i className="ri-grid-line text-primary-500" />
              内容板块
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex items-start gap-4 p-4 md:p-5 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200/50 transition-all"
                >
                  <div className="w-12 h-12 bg-primary-50 group-hover:bg-primary-500 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors">
                    <i className={`${cat.icon} text-primary-500 group-hover:text-background-50 text-xl transition-colors`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                        {cat.name}
                      </h3>
                      <span className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">
                        {cat.articleCount} 篇
                      </span>
                    </div>
                    <p className="text-xs md:text-sm text-foreground-500 line-clamp-2">
                      {cat.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="mb-12 md:mb-16" aria-labelledby="contact-heading">
            <h2
              id="contact-heading"
              className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4 flex items-center gap-2"
            >
              <i className="ri-mail-line text-primary-500" />
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {/* Contact Info */}
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6">
                <h3 className="font-medium text-foreground-900 mb-4">联系方式</h3>
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500" />
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400 block">邮箱</span>
                      <span className="text-sm text-foreground-800">hello@9288c.com</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-primary-500" />
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400 block">工作时间</span>
                      <span className="text-sm text-foreground-800">周一至周日 9:00-22:00</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-weibo-line text-primary-500" />
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400 block">微博</span>
                      <span className="text-sm text-foreground-800">@黑料吃瓜网官方</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-tiktok-line text-primary-500" />
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400 block">抖音</span>
                      <span className="text-sm text-foreground-800">黑料吃瓜网</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6">
                <h3 className="font-medium text-foreground-900 mb-4">在线留言</h3>
                <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                  <div>
                    <label className="text-xs text-foreground-500 mb-1 block">您的姓名 *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
                      placeholder="请输入姓名"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-foreground-500 mb-1 block">邮箱地址 *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
                      placeholder="请输入邮箱"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-foreground-500 mb-1 block">留言内容 *</label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
                      placeholder="请输入您的留言或爆料内容..."
                    />
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="h-field"
                  />
                  {formStatus === 'error' && (
                    <div className="text-sm text-primary-600 bg-primary-50 rounded-lg px-3 py-2">
                      {formError}
                    </div>
                  )}
                  {formStatus === 'success' && (
                    <div className="text-sm text-secondary-700 bg-secondary-50 rounded-lg px-3 py-2">
                      感谢您的留言，我们会尽快回复！
                    </div>
                  )}
                  <button
                    type="submit"
                    className="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors"
                  >
                    提交留言
                  </button>
                </form>
              </div>
            </div>
          </section>

          {/* Disclaimer */}
          <section id="disclaimer" className="mb-12 md:mb-16" aria-labelledby="disclaimer-heading">
            <h2
              id="disclaimer-heading"
              className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4 flex items-center gap-2"
            >
              <i className="ri-error-warning-line text-primary-500" />
              免责声明
            </h2>
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6">
              <div className="text-sm text-foreground-600 leading-relaxed space-y-3">
                <p>
                  1. 黑料吃瓜网（以下简称"本站"）所提供的所有内容均来源于网络公开信息，仅供用户浏览和娱乐参考，不构成任何事实认定或法律意见。
                </p>
                <p>
                  2. 本站致力于提供娱乐资讯服务，对于所发布内容的真实性、准确性、完整性不做任何形式的担保。用户应理性看待本站内容，自行判断信息真伪。
                </p>
                <p>
                  3. 若本站内容涉及任何个人或组织的合法权益，相关权利人可通过底部联系方式与我们取得联系。我们将在收到通知后24小时内进行核实，对确有侵权的内容将立即删除。
                </p>
                <p>
                  4. 用户在本站发表的评论仅代表其个人观点，与本站立场无关。本站不对用户评论内容的合法性、准确性负责。
                </p>
                <p>
                  5. 本站内容可能涉及敏感话题，未成年人请在监护人指导下浏览。访问和使用本站即视为同意本免责声明的全部内容。
                </p>
              </div>
            </div>
          </section>

          {/* Privacy Policy */}
          <section id="privacy" className="mb-12 md:mb-16" aria-labelledby="privacy-heading">
            <h2
              id="privacy-heading"
              className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4 flex items-center gap-2"
            >
              <i className="ri-lock-line text-primary-500" />
              隐私政策
            </h2>
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6">
              <div className="text-sm text-foreground-600 leading-relaxed space-y-3">
                <p>
                  1. 本站重视用户的隐私保护。在您使用本站服务时，我们可能会收集必要的使用数据以改善服务质量，但绝不会将您的个人信息出售或泄露给第三方。
                </p>
                <p>
                  2. 我们使用Cookie和类似技术来提供个性化的浏览体验。您可以通过浏览器设置禁用Cookie，但这可能影响部分功能的正常使用。
                </p>
                <p>
                  3. 若您通过本站订阅邮件服务，我们仅将您的邮箱地址用于发送精选吃瓜内容，不会用于其他商业推广目的。您可随时取消订阅。
                </p>
                <p>
                  4. 我们采取行业标准的安全措施保护用户数据，但无法保证绝对安全。如遇数据泄露事件，我们将第一时间通知受影响的用户。
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
